import type { CSSProperties } from 'react';
import { CharacterCount } from '@tiptap/extensions';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Copy,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  PaintBucket,
  Quote,
  Redo,
  RemoveFormatting,
  Strikethrough,
  Underline as UnderlineIcon,
  Undo,
} from 'lucide-react';
import { useEffect, useRef } from 'react';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import {
  convertBrToEmptyParagraphs,
  EXTENDED_EXTENSIONS,
} from './extended-extensions';
import { PastePlain } from './paste-plain';
import css from './rich-text-editor.module.scss';

const styles = pickCss(css, css.root);

// Legacy content spaces sections with bare <br> runs; normalise so the
// schema round-trip keeps the spacing (extended mode only).
const normalizeHtml = (extended: boolean | undefined, html: string): string =>
  extended && html ? convertBrToEmptyParagraphs(html) : html;

type RichTextEditorProps = {
  // Controlled HTML string. Pass `''` for an empty editor.
  value: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  style?: CSSProperties;
  // Read-only renders the same chrome but disables the prosemirror
  // contenteditable; used for preview-like surfaces.
  readOnly?: boolean;
  // Hard character cap (input beyond it is blocked) + a live counter.
  maxLength?: number;
  // Legacy-CMS parity mode: preserves styled divs/spans + style attributes,
  // adds color/align/table/image (with matching toolbar controls) so content
  // written by the legacy CMS round-trips losslessly. Must not change after
  // mount (the tiptap schema is fixed per editor instance).
  extended?: boolean;
  // In extended mode, hide the "Insert image" toolbar button. The image schema
  // node stays registered (so pasted/legacy images still round-trip), only the
  // insert-by-URL control is dropped. Used by surfaces that want v2 formatting
  // parity minus image upload (e.g. the itinerary item Description).
  hideImage?: boolean;
};

type ToolbarButtonProps = {
  active?: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  children: React.ReactNode;
};

const ToolbarButton = ({
  active,
  disabled,
  label,
  onClick,
  children,
}: ToolbarButtonProps) => (
  <button
    aria-label={label}
    aria-pressed={active}
    className={joinCss(css.toolbarBtn, active && css.toolbarBtnActive)}
    disabled={disabled}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

export const RichTextEditor = ({
  value,
  onChange,
  placeholder = 'Start typing…',
  disabled,
  id,
  className,
  style,
  readOnly,
  maxLength,
  extended,
  hideImage,
}: RichTextEditorProps) => {
  const editor = useEditor({
    // StarterKit v3 already bundles the Link extension; configuring a
    // standalone Link causes a duplicate-extension warning and fires
    // onUpdate during init.
    // PastePlain applies in every mode, extended included: `extended` exists
    // to round-trip legacy content already in the DB, not to accept new
    // styling off the clipboard.
    extensions: [
      StarterKit,
      PastePlain,
      ...(maxLength ? [CharacterCount.configure({ limit: maxLength })] : []),
      ...(extended ? EXTENDED_EXTENSIONS : []),
    ],
    content: normalizeHtml(extended, value) || '<p></p>',
    editable: !disabled && !readOnly,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    // Legacy parity: keep whitespace-only structures instead of collapsing
    // them (the legacy editor parsed with `preserveWhitespace: 'full'`).
    ...(extended
      ? { parseOptions: { preserveWhitespace: 'full' as const } }
      : {}),
  });

  // Mirror external value changes into the editor. Track the last value we
  // applied so a plain-text `value` (which getHTML() wraps in <p>…</p>, so a
  // direct compare never matches) doesn't re-run setContent on every render —
  // that thrash made the empty-state placeholder flash over real content.
  const appliedValue = useRef<string | null>(null);
  useEffect(() => {
    if (!editor) return;
    const next = normalizeHtml(extended, value) || '<p></p>';
    if (next === appliedValue.current) return;
    appliedValue.current = next;
    if (editor.getHTML() === next) return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value, extended]);

  // Keep editable state in sync with the prop.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled && !readOnly);
  }, [editor, disabled, readOnly]);

  const isEmpty = editor?.isEmpty ?? !value;

  const handleCopy = () => {
    if (!editor) return;
    void navigator.clipboard?.writeText(editor.getText());
  };

  return (
    <div
      className={styles(
        { isDisabled: !!disabled, isReadOnly: !!readOnly },
        className
      )}
      id={id}
      style={style}
    >
      {!readOnly && (
        <div className={css.toolbar}>
          <ToolbarButton
            active={editor?.isActive('bold')}
            disabled={!editor || disabled}
            label="Bold"
            onClick={() => editor?.chain().focus().toggleBold().run()}
          >
            <Bold size={14} />
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('italic')}
            disabled={!editor || disabled}
            label="Italic"
            onClick={() => editor?.chain().focus().toggleItalic().run()}
          >
            <Italic size={14} />
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('strike')}
            disabled={!editor || disabled}
            label="Strikethrough"
            onClick={() => editor?.chain().focus().toggleStrike().run()}
          >
            <Strikethrough size={14} />
          </ToolbarButton>
          <span className={css.toolbarDivider} />
          <ToolbarButton
            active={editor?.isActive('bulletList')}
            disabled={!editor || disabled}
            label="Bulleted list"
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
          >
            <List size={14} />
          </ToolbarButton>
          <ToolbarButton
            active={editor?.isActive('orderedList')}
            disabled={!editor || disabled}
            label="Numbered list"
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          >
            <ListOrdered size={14} />
          </ToolbarButton>
          <span className={css.toolbarDivider} />
          <ToolbarButton
            active={editor?.isActive('link')}
            disabled={!editor || disabled}
            label="Link"
            onClick={() => {
              if (!editor) return;
              const prev = editor.getAttributes('link').href as
                | string
                | undefined;
              // Skip the prompt UX in v1 — toggling preserves selection text;
              // a paste / autolink already covers the common path.
              if (prev) {
                editor.chain().focus().unsetLink().run();
                return;
              }
              const url = window.prompt('Link URL', 'https://');
              if (!url) return;
              editor.chain().focus().setLink({ href: url }).run();
            }}
          >
            <Link2 size={14} />
          </ToolbarButton>
          {extended && (
            <>
              <span className={css.toolbarDivider} />
              <select
                aria-label="Heading level"
                className={css.toolbarSelect}
                disabled={!editor || disabled}
                onChange={(e) => {
                  const level = Number(e.target.value);
                  if (level === 0) {
                    editor?.chain().focus().setParagraph().run();
                  } else {
                    editor
                      ?.chain()
                      .focus()
                      .toggleHeading({ level: level as 1 | 2 | 3 })
                      .run();
                  }
                }}
                value={
                  editor?.isActive('heading', { level: 1 })
                    ? 1
                    : editor?.isActive('heading', { level: 2 })
                      ? 2
                      : editor?.isActive('heading', { level: 3 })
                        ? 3
                        : 0
                }
              >
                <option value={0}>Paragraph</option>
                <option value={1}>Heading 1</option>
                <option value={2}>Heading 2</option>
                <option value={3}>Heading 3</option>
              </select>
              <ToolbarButton
                active={editor?.isActive('underline')}
                disabled={!editor || disabled}
                label="Underline"
                onClick={() => editor?.chain().focus().toggleUnderline().run()}
              >
                <UnderlineIcon size={14} />
              </ToolbarButton>
              <span className={css.toolbarDivider} />
              <ToolbarButton
                active={editor?.isActive({ textAlign: 'left' })}
                disabled={!editor || disabled}
                label="Align left"
                onClick={() =>
                  editor?.chain().focus().setTextAlign('left').run()
                }
              >
                <AlignLeft size={14} />
              </ToolbarButton>
              <ToolbarButton
                active={editor?.isActive({ textAlign: 'center' })}
                disabled={!editor || disabled}
                label="Align center"
                onClick={() =>
                  editor?.chain().focus().setTextAlign('center').run()
                }
              >
                <AlignCenter size={14} />
              </ToolbarButton>
              <ToolbarButton
                active={editor?.isActive({ textAlign: 'right' })}
                disabled={!editor || disabled}
                label="Align right"
                onClick={() =>
                  editor?.chain().focus().setTextAlign('right').run()
                }
              >
                <AlignRight size={14} />
              </ToolbarButton>
              <ToolbarButton
                active={editor?.isActive({ textAlign: 'justify' })}
                disabled={!editor || disabled}
                label="Justify"
                onClick={() =>
                  editor?.chain().focus().setTextAlign('justify').run()
                }
              >
                <AlignJustify size={14} />
              </ToolbarButton>
              <span className={css.toolbarDivider} />
              <ToolbarButton
                active={editor?.isActive('blockquote')}
                disabled={!editor || disabled}
                label="Blockquote"
                onClick={() => editor?.chain().focus().toggleBlockquote().run()}
              >
                <Quote size={14} />
              </ToolbarButton>
              <ToolbarButton
                active={editor?.isActive('code')}
                disabled={!editor || disabled}
                label="Inline code"
                onClick={() => editor?.chain().focus().toggleCode().run()}
              >
                <Code size={14} />
              </ToolbarButton>
              <ToolbarButton
                disabled={!editor || disabled}
                label="Clear formatting"
                onClick={() =>
                  editor?.chain().focus().clearNodes().unsetAllMarks().run()
                }
              >
                <RemoveFormatting size={14} />
              </ToolbarButton>
              <ToolbarButton
                disabled={!editor || disabled}
                label="Copy text"
                onClick={handleCopy}
              >
                <Copy size={14} />
              </ToolbarButton>
              <span className={css.toolbarDivider} />
              <label className={css.toolbarColor} title="Text color">
                <PaintBucket size={14} />
                <input
                  aria-label="Text color"
                  disabled={!editor || disabled}
                  onChange={(e) =>
                    editor?.chain().focus().setColor(e.target.value).run()
                  }
                  type="color"
                  value={
                    (editor?.getAttributes('textStyle').color as string) ??
                    '#000000'
                  }
                />
              </label>
              <select
                aria-label="Table actions"
                className={css.toolbarSelect}
                disabled={!editor || disabled}
                onChange={(e) => {
                  const chain = editor?.chain().focus();
                  if (!chain) return;
                  switch (e.target.value) {
                    case 'insert':
                      chain
                        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                        .run();
                      break;
                    case 'add-row':
                      chain.addRowAfter().run();
                      break;
                    case 'del-row':
                      chain.deleteRow().run();
                      break;
                    case 'add-col':
                      chain.addColumnAfter().run();
                      break;
                    case 'del-col':
                      chain.deleteColumn().run();
                      break;
                    case 'delete':
                      chain.deleteTable().run();
                      break;
                    default:
                      break;
                  }
                  e.target.value = '';
                }}
                value=""
              >
                <option value="">Table…</option>
                <option value="insert">Insert table</option>
                <option value="add-row">Add row</option>
                <option value="del-row">Delete row</option>
                <option value="add-col">Add column</option>
                <option value="del-col">Delete column</option>
                <option value="delete">Delete table</option>
              </select>
              {!hideImage && (
                <ToolbarButton
                  disabled={!editor || disabled}
                  label="Insert image"
                  onClick={() => {
                    const url = window.prompt('Image URL', 'https://');
                    if (!url) return;
                    editor?.chain().focus().setImage({ src: url }).run();
                  }}
                >
                  <ImageIcon size={14} />
                </ToolbarButton>
              )}
            </>
          )}
          <span className={css.toolbarDivider} />
          <ToolbarButton
            disabled={!editor || disabled || !editor.can().undo()}
            label="Undo"
            onClick={() => editor?.chain().focus().undo().run()}
          >
            <Undo size={14} />
          </ToolbarButton>
          <ToolbarButton
            disabled={!editor || disabled || !editor.can().redo()}
            label="Redo"
            onClick={() => editor?.chain().focus().redo().run()}
          >
            <Redo size={14} />
          </ToolbarButton>
        </div>
      )}
      <div className={css.body}>
        <EditorContent editor={editor} />
        {isEmpty && placeholder && (
          <div aria-hidden="true" className={css.placeholder}>
            {placeholder}
          </div>
        )}
      </div>
      {maxLength && !readOnly && (
        <div className={css.charCount}>
          {editor?.storage.characterCount?.characters() ?? 0} / {maxLength}
        </div>
      )}
    </div>
  );
};
